import React, { CSSProperties, forwardRef, HTMLAttributes, KeyboardEvent, ReactNode, RefObject, useCallback, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';

import CrossIcon from '@/components/icons/Cross';

const Veil = styled('div')({
  position: 'fixed',
  inset: 0,
  background: '#00000080',
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  justifyContent: 'center',
  alignItems: 'center'
});

const Window = styled('div')({
  width: '455px',
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  borderRadius: '10px',
  border: '1px solid #DFE1E6',
  padding: '16px',
  gap: '16px',
  background: '#FFFFFF',
  boxShadow: '0px 0px 20px 0px #0000004D',
  maxHeight: '100%',
});

const Header = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'space-between',
  gap: '10px',
  alignItems: 'center'
});

const Title = styled('h1')({
  margin: '0',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: '#091E42',
  fontWeight: 500,
  fontSize: '20px',
});

const CloseButton = styled('button')({
  width: '28px',
  height: '28px',
  fontSize: '18px',
  padding: '5px',
  border: 'none',
  background: 'transparent',
  color: '#C1C7D0',
  ['&:hover']: {
    color: '#091E42',
  },
  ['&:disabled']: {
    color: 'transparent',
  },
});

const Body = styled('div')({
  margin: 0,
  display: 'flex',
  gap: '10px',
  flexDirection: 'column',
  minHeight: '0',
  maxHeight: '100%',
  flex: '1 1 auto',
  overflow: 'auto',
});

const Question = styled('span')({
  color: '#091E42',
  fontWeight: 400,
  fontSize: '13px',
  flexShrink: 0,
  whiteSpace: 'pre-line',
});

const ActionsBar = styled('div')({
  display: 'flex',
  gap: '10px',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
});

const ActionButton = styled('button')({
  height: '32px',
  padding: '9px 16px',
  fontWeight: 500,
  fontSize: '12px',
  borderRadius: '6px',
  border: '1px solid #DFE1E6',
  background: 'transparent',
  color: '#091E42',
  ['&:disabled']: {
    opacity: 0.5,
  },
  ['&:hover']: {
    cursor: 'pointer',
  }
});

export type PromptProps = {
  open: boolean,
  title: string,
  question?: string | ReactNode,
  onClose?: string | (() => void),
  onEnter?: string | (() => void),
  actions: Array<{
    name: string,
    handler: () => unknown,
    style?: CSSProperties,
    caption: string,
  }>,
  disabled?: boolean
} & HTMLAttributes<HTMLDivElement>;

export default forwardRef(
  (
    { open, title, question, children, actions, onClose, onEnter, disabled, ...props }: PromptProps,
    mainRef: RefObject<HTMLDivElement>
  ) => {
    const veilRef = useRef<HTMLDivElement>(null);

    const keyDownHandler = useCallback((event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.key === 'Enter') {
        if (typeof onEnter === 'function') {
          return onEnter();
        }
        for (const { name, handler } of actions) {
          if (onEnter === name) {
            return handler();
          }
        }
      }
    }, [ onEnter, actions, disabled ]);

    const closeHandler = useCallback(() => {
      if (typeof onClose === 'function') {
        return onClose();
      }
      for (const { name, handler } of actions) {
        if (onClose === name) {
          return handler();
        }
      }
    }, [ onClose, actions ]);

    useEffect(() => {
      if (veilRef?.current) {
        if (open) {
          veilRef.current.focus();
        } else {
          veilRef.current.blur();
        }
      }
    }, [ open, veilRef?.current ]);
    
    return <Veil
      ref={veilRef}
      onKeyDown={keyDownHandler}
      tabIndex={1}
      style={!open ? { display: 'none' } : {}}
    >
      <Window
        ref={mainRef}
        {...props}
      >
        <Header>
          <Title>{title}</Title>
          {onClose && (
            <CloseButton
              onClick={closeHandler}
              disabled={disabled}
            >
              <CrossIcon/>
            </CloseButton>
          )}
        </Header>

        <Body>
          {question && (<Question>{question}</Question>)}
          {children}
        </Body>

        {actions.length && (
          <ActionsBar>
            {actions.map(({ name, handler, style, caption }, i) => (
              <ActionButton
                key={`${name}-${i}`}
                onClick={handler}
                style={style}
                disabled={disabled}
              >
                {caption}
              </ActionButton>
            ))}
          </ActionsBar>
        )}
      </Window>
    </Veil>
  }
);